import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '~/auth';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

import type { AiToolsSectionRegistration } from './AiToolsContext';
import PromoAiEnvironmentNotice from './PromoAiEnvironmentNotice';
import type { ThreadContextSnapshot } from './chatContext';
import { useAdminChatContextSnapshot } from './useAdminChatContextSnapshot';

const PANEL_DEFAULT_OFFSET = 16;
const PANEL_VIEWPORT_GUTTER = PANEL_DEFAULT_OFFSET * 2;
const DEFAULT_PANEL_WIDTH = 440;
const MIN_PANEL_WIDTH = 360;
const ADMIN_APP_BAR_SELECTOR = 'header.MuiAppBar-root';
const PANEL_WIDTH_SESSION_STORAGE_KEY = 'aiOpsChatPanelWidth';

export interface AiChatPromptRequest {
  prompt: string;
  requestId: number;
  fallbackDepartment: string | null;
}

type AiOpsChatEmbedMessage =
  | { type: 'ai-ops-chat:auth-token'; token: string }
  | { type: 'ai-ops-chat:context'; context: ThreadContextSnapshot }
  | ({ type: 'ai-ops-chat:prompt' } & AiChatPromptRequest);

interface Props {
  promptRequest?: AiChatPromptRequest | null;
  toolSection: AiToolsSectionRegistration | null;
  onToolsContainer: (container: HTMLDivElement | null) => void;
}

function buildEmbedUrl(origin: string): string {
  const url = new URL('/embed/chat', origin);
  url.searchParams.set('source', 'admin');

  return url.toString();
}

function getPanelTopOffset(): number {
  return Math.max(
    0,
    Math.round(document.querySelector<HTMLElement>(ADMIN_APP_BAR_SELECTOR)?.getBoundingClientRect().bottom ?? 0),
  );
}

function getMaxPanelWidth(): number {
  return Math.max(MIN_PANEL_WIDTH, window.innerWidth - PANEL_VIEWPORT_GUTTER);
}

function clampPanelWidth(width: number): number {
  return Math.min(Math.max(width, MIN_PANEL_WIDTH), getMaxPanelWidth());
}

function getInitialPanelWidth(): number {
  const storedWidth = Number(window.sessionStorage.getItem(PANEL_WIDTH_SESSION_STORAGE_KEY));

  return clampPanelWidth(Number.isFinite(storedWidth) && storedWidth > 0 ? storedWidth : DEFAULT_PANEL_WIDTH);
}

export default function AiOpsChatIframeDrawer({ promptRequest = null, toolSection, onToolsContainer }: Props) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const launcherRef = useRef<HTMLButtonElement | null>(null);
  const { getAccessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'chat' | 'tools' | null>('chat');
  const [isEmbedReady, setIsEmbedReady] = useState(false);
  const postedPromptRequestIdRef = useRef<number | null>(null);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [panelTopOffset, setPanelTopOffset] = useState(getPanelTopOffset);
  const [panelWidth, setPanelWidth] = useState(getInitialPanelWidth);
  const contextSnapshot = useAdminChatContextSnapshot();
  const aiOpsChatOrigin = window.configs?.AI_OPS_CHAT_ORIGIN || null;
  const currentEmbedUrl = useMemo(() => (aiOpsChatOrigin ? buildEmbedUrl(aiOpsChatOrigin) : null), [aiOpsChatOrigin]);

  const postToIframe = useCallback(
    (message: AiOpsChatEmbedMessage) => {
      if (!aiOpsChatOrigin) {
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(message, aiOpsChatOrigin);
    },
    [aiOpsChatOrigin],
  );

  const postAuthToken = useCallback(async () => {
    const token = await getAccessToken().catch(() => null);

    if (token) {
      postToIframe({ type: 'ai-ops-chat:auth-token', token });
    }
  }, [getAccessToken, postToIframe]);

  const postContext = useCallback(() => {
    if (contextSnapshot) {
      postToIframe({ type: 'ai-ops-chat:context', context: contextSnapshot });
    }
  }, [contextSnapshot, postToIframe]);

  const postAuthTokenAndContext = useCallback(() => {
    void postAuthToken();
    postContext();
  }, [postAuthToken, postContext]);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!promptRequest || !isEmbedReady || postedPromptRequestIdRef.current === promptRequest.requestId) {
      return;
    }

    postedPromptRequestIdRef.current = promptRequest.requestId;
    setIsOpen(true);
    setExpandedSection('chat');
    postToIframe({ type: 'ai-ops-chat:prompt', ...promptRequest });
  }, [isEmbedReady, postToIframe, promptRequest]);

  useEffect(() => {
    if (!toolSection) {
      setExpandedSection((current) => (current === 'tools' ? 'chat' : current));
    }
  }, [toolSection]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        window.requestAnimationFrame(() => launcherRef.current?.focus());
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const updatePanelWidth = useCallback((nextWidth: number) => {
    const clampedWidth = clampPanelWidth(nextWidth);
    setPanelWidth(clampedWidth);
    window.sessionStorage.setItem(PANEL_WIDTH_SESSION_STORAGE_KEY, String(clampedWidth));
  }, []);

  const resizePanel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        updatePanelWidth(window.innerWidth - event.clientX);
      }
    },
    [updatePanelWidth],
  );

  useEffect(() => {
    if (!iframeSrc && currentEmbedUrl) {
      setIframeSrc(currentEmbedUrl);
    }
  }, [currentEmbedUrl, iframeSrc]);

  useEffect(() => {
    void postAuthToken();
  }, [postAuthToken]);

  useEffect(() => {
    postContext();
  }, [postContext]);

  useEffect(() => {
    function syncPanelLayout() {
      setPanelTopOffset(getPanelTopOffset());
      setPanelWidth((currentWidth) => clampPanelWidth(currentWidth));
    }

    syncPanelLayout();
    window.addEventListener('resize', syncPanelLayout);
    return () => window.removeEventListener('resize', syncPanelLayout);
  }, []);

  useEffect(() => {
    if (!aiOpsChatOrigin) {
      return undefined;
    }

    function handleMessage(event: MessageEvent) {
      if (event.origin !== aiOpsChatOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'ai-ops-chat:close') {
        setIsOpen(false);
        return;
      }

      if (event.data?.type === 'ai-ops-chat:ready') {
        setIsEmbedReady(true);
        postAuthTokenAndContext();
      }

      if (event.data?.type === 'ai-ops-chat:auth-required') {
        void postAuthToken();
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [aiOpsChatOrigin, postAuthToken, postAuthTokenAndContext]);

  if (!currentEmbedUrl) {
    return null;
  }

  return (
    <>
      {!isOpen ? (
        <Button
          variant="contained"
          size="small"
          onClick={openChat}
          ref={launcherRef}
          aria-label="Open AI tools"
          disableElevation
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 99999,
            width: 32,
            minWidth: 32,
            minHeight: 92,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            py: 1,
            px: 0.25,
            overflow: 'hidden',
            borderRadius: '12px 0 0 12px',
            boxShadow: (theme) =>
              `0 14px 32px -14px ${alpha(theme.palette.primary.main, 0.75)}, 0 4px 10px -4px ${alpha(
                theme.palette.primary.main,
                0.45,
              )}`,
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 46%, ${theme.palette.primary.dark} 100%)`,
            transition: (theme) =>
              theme.transitions.create(['box-shadow', 'filter', 'transform', 'width', 'min-width'], {
                duration: theme.transitions.duration.shorter,
              }),
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(255,255,255,0.2), transparent 44%)',
              pointerEvents: 'none',
            },
            '&:hover': {
              filter: 'brightness(1.05)',
              transform: 'translateY(-50%)',
              width: 36,
              minWidth: 36,
              boxShadow: (theme) =>
                `0 18px 36px -14px ${alpha(theme.palette.primary.main, 0.85)}, 0 6px 14px -4px ${alpha(
                  theme.palette.primary.main,
                  0.5,
                )}`,
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${alpha(theme.palette.primary.light, 0.8)}`,
              outlineOffset: 2,
            },
            '& .ai-chat-tab-label': {
              position: 'relative',
              zIndex: 1,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              lineHeight: 1,
              textTransform: 'uppercase',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
            },
          }}
        >
          <AutoAwesomeOutlinedIcon
            sx={{
              position: 'relative',
              zIndex: 1,
              boxSizing: 'content-box',
              p: 0.35,
              fontSize: 15,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid rgba(255, 255, 255, 0.28)',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.24)',
            }}
          />
          <span className="ai-chat-tab-label">AI tools</span>
        </Button>
      ) : null}

      <Paper
        elevation={0}
        component="aside"
        aria-label={isOpen ? 'AI tools panel' : undefined}
        aria-hidden={!isOpen}
        sx={{
          position: 'fixed',
          right: 0,
          top: `${panelTopOffset}px`,
          bottom: 0,
          width: `${panelWidth}px`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0,
          borderLeft: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '-16px 0 32px -24px rgba(15, 23, 42, 0.45)',
          zIndex: isOpen ? 1300 : -1,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          visibility: isOpen ? 'visible' : 'hidden',
        }}
      >
        <Box
          role="separator"
          aria-label="Resize AI tools panel"
          aria-orientation="vertical"
          aria-valuemin={MIN_PANEL_WIDTH}
          aria-valuemax={getMaxPanelWidth()}
          aria-valuenow={panelWidth}
          onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
          onPointerMove={resizePanel}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: -4,
            width: 8,
            cursor: 'ew-resize',
            touchAction: 'none',
            zIndex: 1,
          }}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1">AI tools</Typography>
          <IconButton aria-label="Close AI tools" onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Accordion
          expanded={expandedSection === 'chat'}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'chat' : null)}
          disableGutters
          square
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: expandedSection === 'chat' ? 1 : 'none',
            minHeight: 0,
            '& .MuiCollapse-root': { flex: expandedSection === 'chat' ? 1 : 'none', minHeight: 0, overflow: 'auto' },
            '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiAccordion-region': { height: '100%' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ai-tools-chat-content"
            id="ai-tools-chat-header"
          >
            <Typography variant="subtitle2">Chat</Typography>
          </AccordionSummary>
          <AccordionDetails id="ai-tools-chat-content" sx={{ p: 0, height: '100%' }}>
            <iframe
              ref={iframeRef}
              title="AI Ops chatbot"
              src={iframeSrc || currentEmbedUrl}
              allow="clipboard-write"
              onLoad={postAuthTokenAndContext}
              style={{
                border: 0,
                height: '100%',
                minHeight: 0,
                width: '100%',
              }}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedSection === 'tools' && Boolean(toolSection)}
          onChange={(_, expanded) => setExpandedSection(expanded ? 'tools' : null)}
          disableGutters
          square
          elevation={0}
          sx={{
            display: toolSection ? 'flex' : 'none',
            flexDirection: 'column',
            flex: expandedSection === 'tools' ? 1 : 'none',
            minHeight: 0,
            '& .MuiCollapse-root': { flex: expandedSection === 'tools' ? 1 : 'none', minHeight: 0, overflow: 'auto' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="ai-tools-workflow-content"
            id="ai-tools-workflow-header"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{toolSection?.title}</Typography>
              {toolSection && <PromoAiEnvironmentNotice />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails id="ai-tools-workflow-content">
            <Box ref={onToolsContainer} />
          </AccordionDetails>
        </Accordion>
      </Paper>
    </>
  );
}
